'use client';

import { useEffect, useState } from 'react';
import { formatPrice, Tier, TIER_BG } from '@/lib/game-logic';
import { useTranslation } from '@/lib/i18n/LanguageContext';

interface PriceGaugeProps {
  guess: number;
  actual: number;
  tier: Tier;
}

/** Horizontal scale showing where the guess landed relative to the real price. */
export function PriceGauge({ guess, actual, tier }: PriceGaugeProps) {
  const { t } = useTranslation();
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setRevealed(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const max = Math.max(guess, actual, 1) * 1.25;
  const pct = (v: number) => (v / max) * 100;
  const lo = Math.min(guess, actual);
  const hi = Math.max(guess, actual);

  return (
    <div
      className="px-10 py-8"
      role="img"
      aria-label={`${t('yourAnswer')} ${formatPrice(guess)} / ${t('actualPrice')} ${formatPrice(actual)}`}
    >
      <div className="relative h-2 rounded-full bg-surface-2">
        {/* distance between guess and actual */}
        <div
          className={`absolute inset-y-0 rounded-full ${TIER_BG[tier]} transition-transform duration-700 ease-out`}
          style={{
            left: `${pct(lo)}%`,
            width: `${pct(hi - lo)}%`,
            transformOrigin: guess <= actual ? 'left' : 'right',
            transform: revealed ? 'scaleX(1)' : 'scaleX(0)',
            transitionDelay: '350ms',
          }}
        />

        {/* guess marker (label above) */}
        <div
          className="absolute top-1/2 transition-opacity duration-500"
          style={{ left: `${pct(guess)}%`, opacity: revealed ? 1 : 0 }}
        >
          <span className="absolute bottom-3.5 left-0 -translate-x-1/2 whitespace-nowrap text-[11px] font-semibold uppercase tracking-widest text-muted">
            {t('you')}
          </span>
          <span className="absolute left-0 top-0 block h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-fg bg-ink" />
        </div>

        {/* actual marker (label below) */}
        <div
          className="absolute top-1/2 transition-opacity duration-500"
          style={{ left: `${pct(actual)}%`, opacity: revealed ? 1 : 0, transitionDelay: '350ms' }}
        >
          <span className="absolute left-0 top-0 block h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent ring-4 ring-accent/25" />
          <span className="absolute left-0 top-3.5 -translate-x-1/2 whitespace-nowrap font-mono text-sm font-bold text-accent">
            {formatPrice(actual)}
          </span>
        </div>
      </div>
    </div>
  );
}
